import type { AdSlotType } from '../../advertising/AdTypes';
import type { AssetId } from '../../assets/AssetTypes';
import type { DynamicEventType } from '../../dynamic-events/DynamicEventType';
import type { HazardDefinition } from '../../hazards/HazardDefinition';
import type { ObstacleType } from '../../obstacles/ObstacleType';
import type { Vec3 } from '../../utils/math';
import type { ConnectorDefinition } from '../connectors/ConnectorDefinition';

/**
 * Data-only description of a track module. Everything here is plain,
 * serializable data so definitions can later be generated from Blender
 * exports (JSON) without touching gameplay code.
 *
 * Local space: origin = CONNECTOR_IN at ground level on the track centerline,
 * +Z = direction of travel, +X = right, +Y = up. Units: meters.
 */
export const SegmentKind = {
  Straight: 'STRAIGHT',
  TurnLeft: 'TURN_LEFT',
  TurnRight: 'TURN_RIGHT',
  TJunction: 'T_JUNCTION',
  Crossing: 'CROSSING',
  /** Smooth curve without input (SEG_15/16): the centre line is an arc, see `curve`. */
  CurveLeft: 'CURVE_LEFT',
  CurveRight: 'CURVE_RIGHT',
} as const;
export type SegmentKind = (typeof SegmentKind)[keyof typeof SegmentKind];

export interface LaneDefinition {
  /** 0 = left, 1 = center, 2 = right (for 3 lanes). */
  readonly index: number;
  readonly id: 'LEFT' | 'CENTER' | 'RIGHT';
  readonly centerX: number;
  readonly width: number;
}

export interface ObstacleSpawnPoint {
  /** e.g. SOCKET_OBS_A_L0 – also the Blender empty name. */
  readonly id: string;
  /** Spawn points with the same row are at the same Z and form one decision line. */
  readonly row: string;
  readonly lane: number;
  readonly position: Vec3;
  readonly allowedTypes: readonly ObstacleType[];
}

export interface CollectibleSpawnPoint {
  readonly id: string;
  readonly lane: number;
  /** Base position (bottom center of the collectible). */
  readonly position: Vec3;
  /**
   * Collectible line this point belongs to (e.g. a zig-zag across lanes). If a segment
   * defines lines, the populator picks one line instead of one whole lane.
   */
  readonly line?: string;
}

export interface DecorationSocket {
  readonly id: string;
  /** FAR = beyond the closed end of a junction, facing the runner. */
  readonly side: 'LEFT' | 'RIGHT' | 'FAR';
  readonly position: Vec3;
  readonly yaw: number;
  /** Space a decoration may occupy (meters) – asset must fit. */
  readonly maxFootprint: { readonly width: number; readonly depth: number };
  /** Tags filter which decoration assets fit (e.g. "ground", "tall"). */
  readonly tags: readonly string[];
  /** Overrides LevelConfig.decorationFillChance (e.g. rare cranes in the backdrop). */
  readonly fillChance?: number;
}

export interface DynamicEventSocket {
  readonly id: string;
  readonly position: Vec3;
  readonly yaw: number;
  readonly supportedEvents: readonly DynamicEventType[];
  /** Event is triggered when the player is this far before the socket (meters). */
  readonly triggerDistance: number;
  readonly affectedLanes: readonly number[];
}

export interface AdvertisingSlotDefinition {
  /** Slot id inside the segment (e.g. AD_FENCE_LEFT). */
  readonly id: string;
  readonly slotType: AdSlotType;
  /** Center of the visible surface. */
  readonly position: Vec3;
  /** Direction the visible side faces (towards the track). */
  readonly yaw: number;
  readonly width: number;
  readonly height: number;
  /** Overrides AdvertisingConfig.slotFillRate – e.g. 1 for the big banner at a closed junction end. */
  readonly fillRate?: number;
}

/**
 * Junction data for turn/intersection modules. The path bends at `pivot`
 * (center of the junction square); side exits sit on the square's edge so
 * every exit path has the same length as the segment.
 */
export interface JunctionDefinition {
  /** Blender empty: JUNCTION_PIVOT. */
  readonly pivot: Vec3;
}

/** Hand-designed obstacle placement (set pieces) – replaces random generation for that segment. */
export interface FixedObstaclePlacement {
  readonly spawnPointId: string;
  readonly obstacleId: string;
}

/**
 * Hand-placed collectible of a set piece (the golden bottle of SEG_GOLDEN_RAMP_60M): placed every time
 * the module is, independent of the random bottle lines. Its spawn point belongs to no line.
 */
export interface FixedCollectiblePlacement {
  readonly spawnPointId: string;
  readonly collectibleId: string;
  /** Size factor of the visual and the pickup volume (default 1). */
  readonly scale?: number;
  /** Placed with this probability (own random stream per placement); unset = every time. */
  readonly chance?: number;
  /** Placed only from this run distance on (the beer crate: LevelConfig.turboCrates.fromDistance). */
  readonly minDistance?: number;
}

/**
 * One hand-designed obstacle layout of a segment. The populator picks one variant per
 * placement (gameplay RNG); every row still runs through the SequenceValidator and a
 * row that does not fit the preceding track is left out (its terrain stays).
 */
export interface ObstacleVariant {
  readonly id: string;
  readonly weight: number;
  readonly placements: readonly FixedObstaclePlacement[];
  /** Moving hazards (ids of `SegmentDefinition.hazards`) active in this layout – default: all. */
  readonly hazards?: readonly string[];
  /**
   * Difficulty: the layout is only picked in tiers with this row layout (DifficultyTier.rowLayout –
   * sparse = warm-up tier, dense = later tiers) – default: in every tier.
   */
  readonly rowLayout?: 'sparse' | 'dense';
  /**
   * Collectible lines (`CollectibleSpawnPoint.line`) that suit this layout – e.g. the risk line through
   * its combos. The populator picks one of them – default: any line of the segment.
   */
  readonly collectibleLines?: readonly string[];
}

// ── Terrain ────────────────────────────────────────────────────────────────
// Static walkable geometry of a segment. All values in segment-local space
// (x lateral, y height, z along the path); evaluated by level/terrain.

/** Rectangle on the segment's floor plan: z along the path, x lateral (half-open ranges). */
export interface TrackArea {
  readonly zFrom: number;
  readonly zTo: number;
  readonly xFrom: number;
  readonly xTo: number;
}

/**
 * Walkable surface: platform (heightFrom = heightTo) or ramp (height linear along z).
 * Solid underneath – its edges are walls for a runner below the top. Surfaces take
 * precedence over gaps (a plank over a pit is a bridge).
 */
export interface GroundSurface extends TrackArea {
  /** e.g. SURF_RAMP – later also the Blender collision proxy name. */
  readonly id: string;
  readonly heightFrom: number;
  readonly heightTo: number;
  /**
   * The raised route goes on into the next module (the container tower's column onto the shell's upper floor,
   * generation.followedBy): it may reach OUT – the only exception to "segments connect at ground level".
   */
  readonly leadsOnward?: boolean;
}

/** No ground (pit, gap): a runner over it falls. */
export interface GroundGap extends TrackArea {
  /** e.g. GAP_PIT */
  readonly id: string;
  /** Depth of the pit floor below the surrounding ground (visual, fall animation). */
  readonly depth: number;
}

/**
 * Overhead structure, solid from `clearance` upwards (scaffold crossbars and decks,
 * pipe roofs). A clearance below the sliding height makes it a full-height wall
 * (e.g. the shell of a walk-in pipe or a post).
 */
export interface CeilingZone extends TrackArea {
  /** e.g. CEIL_BAR_01 */
  readonly id: string;
  /** Height of the underside (segment-local y). */
  readonly clearance: number;
  /**
   * Soft ceilings (high decks, reachable only with a double jump) never cause a frontal
   * hit: a runner meeting their edge is pushed below them (head bump).
   */
  readonly soft?: boolean;
  /**
   * Slab: solid only from `clearance` up to this height, and its top is walkable – a runner who
   * gets up there (feet at most a step below it) stands and runs on it (crown of a walk-in pipe).
   * For everyone lower it stays a ceiling / wall. Without `top` the zone is solid all the way up –
   * only where the model visibly is too (above a double jump's reach): a low element's top is where
   * its model ends (pipe bridge, beam, crossbar – SEGMENT_STANDARD "Sprungehrlichkeit").
   */
  readonly top?: number;
  /**
   * An upper floor (the shell's slab top) – reached from the module before (the container tower's column) and left
   * into the next: walkable like any `top`, but no crossing and no deck of this module (no climb, no drop zone here).
   */
  readonly upperFloor?: boolean;
}

export interface SegmentTerrain {
  readonly surfaces: readonly GroundSurface[];
  readonly gaps: readonly GroundGap[];
  readonly ceilings: readonly CeilingZone[];
}

/** Additional visual placed in segment-local space (e.g. two base road modules under a 40 m set piece). */
export interface SegmentVisualPart {
  readonly assetId: AssetId;
  readonly position: Vec3;
  /** Rotation about +Y (radians, Babylon convention: +90° turns local +x to −z) – e.g. a container lying along the track. */
  readonly yaw?: number;
}

/** One piece of a collapsing building: where it stands before the collapse and how it lands. */
export interface CollapsePiece {
  /** Fixed obstacle placement this piece becomes after the collapse. */
  readonly spawnPointId: string;
  /** Pose inside the standing building (segment-local, rotation in degrees). */
  readonly buildingPosition: Vec3;
  readonly buildingRotation: Vec3;
  /** Slight final yaw for an organic look (degrees) – collision stays the obstacle's box. */
  readonly finalYaw: number;
  /** Start delay after the collapse is triggered (seconds). */
  readonly delay: number;
}

export interface CollapseEventDefinition {
  /** Dynamic event socket (WALL_COLLAPSE) that triggers the collapse. */
  readonly socketId: string;
  readonly pieces: readonly CollapsePiece[];
}

export interface SegmentGenerationRules {
  /** Relative pick weight among compatible segments. */
  readonly weight: number;
  /** Earliest run distance at which the segment may appear. */
  readonly minDistance: number;
  /** From this run distance on the segment no longer appears (a denser variant takes over – the swing cranes). */
  readonly maxDistance?: number;
  /** Minimum run distance between two appearances of this segment (meters). */
  readonly cooldownDistance?: number;
  /**
   * Id of the module that must come right after this one (a pair: the container tower whose column leads onto the
   * shell's upper floor). LevelGenerator.pickNext places it whatever its own rules say.
   */
  readonly followedBy?: string;
  /** Straight segments required since the last junction before this one may appear. */
  readonly minSegmentsAfterJunction?: number;
  /**
   * Tighter heading limit than the junction rule (degrees from the start direction) for this
   * module's exits – e.g. 45° curves only lead back towards the start axis once it is left.
   */
  readonly maxExitHeadingDegrees?: number;
  /**
   * Segments of the same family (e.g. all small-pit variants) never follow each other
   * directly – runs alternate segment types.
   */
  readonly family?: string;
  /**
   * Back-to-back runs of this module (pendulum crane, user 2026-10-03: "manchmal zwei, manchmal vier oder fünf
   * hintereinander, per Zufallsgenerator"): where it follows another module, a run length is drawn and the module
   * follows itself until the run is complete – the family rule waits meanwhile. A scheduled set piece that is due,
   * an overdue landmark or a rule that no longer lets it follow (calm stretch behind a landmark) ends the run early.
   */
  readonly chain?: ModuleChain;
  /** Landmark module (ad gantry): on open straights at a regular spacing – see LandmarkRules. */
  readonly landmark?: LandmarkRules;
  /** Scheduled set piece (golden bottle ramp): once at each mark of the run – see SetPieceSchedule. */
  readonly schedule?: SetPieceSchedule;
  /** A long ride starts behind this module (ructec-Bulli): the road it needs – see VehicleRideRules. */
  readonly ride?: VehicleRideRules;
  readonly tags: readonly string[];
}

/**
 * Run lengths of a module that repeats back to back (SegmentGenerationRules.chain): `[modules in a row, weight]`.
 * The length of each run is drawn from its own random stream, seeded with the run seed and the run's start distance
 * (reproducible per seed, no draw from the track stream).
 */
export interface ModuleChain {
  readonly lengths: readonly (readonly [count: number, weight: number])[];
  /**
   * Longer runs further into the run (user 2026-10-04: "ab 2–3.000 m ruhig mal vier oder fünf Kabelrollen …, die Kräne
   * mehrfach und dichter"): from `distance` on (run distance at the start of the run) these lengths replace `lengths`;
   * the last stage reached counts. Ascending by distance.
   */
  readonly from?: readonly { readonly distance: number; readonly lengths: readonly (readonly [count: number, weight: number])[] }[];
}

/** The window of one mark (run distances, meters) – computed from the schedule (level/generation/setPieceSchedule.ts). */
export interface SetPieceWindow {
  /** The set piece is due from here on (the mark). */
  readonly from: number;
  /** End of the window: from here on it no longer waits for the sight line, only for a quiet predecessor. */
  readonly latestStart: number;
}

/** A stretch of a set-piece schedule: a mark at `from`, then every `every` meters up to `until` (inclusive). */
export interface SetPieceStretch {
  readonly from: number;
  readonly every: number;
  /** Last mark of the stretch – none: endless (only the last stretch). */
  readonly until?: number;
}

/**
 * Placement of a scheduled set piece (SEG_GOLDEN_RAMP_60M), LevelGenerator: never picked at random,
 * placed exactly once at each mark of the run, in order – the n-th placement of the run serves the n-th
 * mark (endless with an open last stretch). From the mark on, only modules that keep the sight line
 * follow until it stands; it needs a quiet predecessor and `sightLine` meters of open straight. It first
 * takes the share of the calm modules (moving hazards keep their frequency) and the spot at once from
 * `mark + forceAfter` on. A run that gets to `mark + window` without placing it no longer waits for the
 * sight line: later, never skipped. The first mark is `minDistance`; a changed minDistance (testing aid
 * ?segment=) moves the whole schedule by the same amount.
 */
export interface SetPieceSchedule {
  /** Ascending stretches of marks; the last one may be open-ended. */
  readonly stretches: readonly SetPieceStretch[];
  /** Meters after each mark in which the set piece should start (it waits for the sight line only within). */
  readonly window: number;
  /** Meters after a mark from which a set piece that fits is placed at once (before: the calm modules' share). */
  readonly forceAfter: number;
  /** Open straight (meters, keepsSightLine) right before the module – the set piece is seen from far. */
  readonly sightLine: number;
  /**
   * Set pieces never crowd each other (LevelConfig.setPieces.minGap): where two schedules meet, the one with the
   * higher priority keeps its mark and the other waits until it stands (later, never skipped). Default 0.
   */
  readonly priority?: number;
}

/**
 * Placement of a landmark (SEG_AD_GANTRY_40M), LevelGenerator: it may stand after `sightLine` meters
 * of open straight (keepsSightLine) behind a quiet module (no junction, moving hazard or scripted event –
 * isQuietModule), at least `cooldownDistance` after the previous one. It then takes the pick share of
 * the plain straights, so the other modules keep their frequency. The first one stands at `minDistance`;
 * with `targetSpacing` the generator also reserves the open straight and places it by then at the latest.
 * The `calmAfter` meters behind it take calm modules only (isCalmModule).
 */
export interface LandmarkRules {
  /** Open straight (meters) required right before the module (0: the module's own approach suffices). */
  readonly sightLine: number;
  /** Track (meters) after the module that only calm modules may fill. */
  readonly calmAfter: number;
  /** Optional: distance to the previous landmark (module start to start) by which the next one is forced. */
  readonly targetSpacing?: number;
}

export interface SegmentDefinition {
  readonly id: string;
  readonly kind: SegmentKind;
  readonly displayName: string;
  /** Path length along the centerline (meters). */
  readonly length: number;
  /** Total drivable width (meters). */
  readonly width: number;
  readonly visualAssetId: AssetId;
  /** Further visuals of the module (e.g. base road modules under a greybox set piece). */
  readonly extraVisuals?: readonly SegmentVisualPart[];

  readonly entry: ConnectorDefinition;
  readonly exits: readonly ConnectorDefinition[];

  readonly lanes: readonly LaneDefinition[];
  readonly obstacleSpawnPoints: readonly ObstacleSpawnPoint[];
  /** Named row groups: "sparse" = one decision line, "dense" = two. */
  readonly obstacleRowLayouts: { readonly sparse: readonly string[]; readonly dense: readonly string[] };
  readonly collectibleSpawnPoints: readonly CollectibleSpawnPoint[];
  readonly decorationSockets: readonly DecorationSocket[];
  readonly dynamicEventSockets: readonly DynamicEventSocket[];
  readonly advertisingSlots: readonly AdvertisingSlotDefinition[];

  /** Present on turn/intersection modules only. */
  readonly junction?: JunctionDefinition;
  /** Set pieces: fixed obstacle layout instead of random rows. */
  readonly fixedObstacles?: readonly FixedObstaclePlacement[];
  /** Hand-designed layouts, one picked per placement (validated row by row) – instead of random rows. */
  readonly obstacleVariants?: readonly ObstacleVariant[];
  /** Set-piece rewards placed every time (e.g. the golden bottle) – besides the random bottle line. */
  readonly fixedCollectibles?: readonly FixedCollectiblePlacement[];
  /** Ramps, platforms, pits and ceilings. Without terrain the segment is flat ground. */
  readonly terrain?: SegmentTerrain;
  /** Set pieces: a building next to the track collapses into the fixed obstacles. */
  readonly collapseEvent?: CollapseEventDefinition;
  /**
   * Moving hazards (crane loads, excavator, vehicles), each armed by one of the dynamic event
   * sockets. Obstacle variants pick which of them are active.
   */
  readonly hazards?: readonly HazardDefinition[];
  /**
   * Curve modules: the centre line is a circular arc from CONNECTOR_IN (heading +Z) that turns
   * by `angle` over the segment length. All socket and terrain data stay in path coordinates
   * (x lateral, z along the arc) – see segments/segmentPath.ts.
   */
  readonly curve?: CurveDefinition;
  /** Enclosed modules (tunnel, interior, shell building): covered stretch with its own lighting. */
  readonly enclosure?: EnclosureDefinition;
  /** Dozer set piece (SEG_DOZER_60M): the parked dozer a runner jumps onto and drives, and the pit it fills. */
  readonly dozer?: DozerSetPiece;
  /** ructec-Bulli (SEG_BUS_START_40M): where the van waits – the runner gets in on contact and drives (bus/BusManager). */
  readonly bus?: BusSetPiece;

  readonly generation: SegmentGenerationRules;
}

/**
 * Dozer set piece (dozer/, DozerConfig): the parked dozer is an obstacle of the module's layouts – a lane change
 * for a runner who passes it – and a vehicle for one who jumps onto it. Driving, the blade pushes the heaps of the
 * layout (DozerConfig.heapObstacleId) and the earth into the pit at the end of the module.
 */
/** ructec-Bulli set piece: the van stands in `lane`, its ground centre at `parkZ` (segment-local). */
export interface BusSetPiece {
  readonly lane: number;
  readonly parkZ: number;
}

/**
 * A set piece the runner rides on for a long stretch (ructec-Bulli, SegmentGenerationRules.ride): for `length` m
 * after the module only modules tagged `roadTag` follow (nothing a vehicle cannot pass), and other set pieces wait
 * until the ride is over.
 */
export interface VehicleRideRules {
  readonly length: number;
  readonly roadTag: string;
  /**
   * The closing module (big ramp): modules tagged `tag` come only – and then first – once the next module starts within
   * `before` m of the ride's end; one finale per ride.
   */
  readonly finale?: { readonly tag: string; readonly before: number };
}

export interface DozerSetPiece {
  /** Obstacle spawn point of the parked dozer (its origin, ground centre of the tracks) – every layout places it. */
  readonly spawnPointId: string;
  /** Terrain gap the dozer fills – across every lane, ahead of its drive. */
  readonly pitGapId: string;
}

export interface CurveDefinition {
  /** Total heading change (radians, + = right / −  = left); the exit yaw equals this angle. */
  readonly angle: number;
}

/** Lighting profile of an enclosed stretch (config: zones.config.ts → enclosures). */
export const EnclosureKind = {
  Tunnel: 'TUNNEL',
  Interior: 'INTERIOR',
  Shell: 'SHELL',
} as const;
export type EnclosureKind = (typeof EnclosureKind)[keyof typeof EnclosureKind];

/** Covered stretch of a module (path z, portal to portal) – the renderer dims the daylight inside. */
export interface EnclosureDefinition {
  readonly kind: EnclosureKind;
  readonly zFrom: number;
  readonly zTo: number;
}
